import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { pushApi } from '../services/api';
import { playNotificationChime, primeNotificationAudio } from '../utils/notificationSound';

export type PushPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

async function resolveVapidPublicKey(): Promise<string> {
  const fromEnv = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (fromEnv) return fromEnv;
  const { publicKey } = await pushApi.getVapidPublicKey();
  return publicKey;
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  return navigator.serviceWorker.ready.then(() => registration);
}

async function syncLocalSubscription(): Promise<boolean> {
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  return !!subscription;
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => {
    console.warn('Service worker registration failed:', err);
  });
}

export function usePushNotifications() {
  const { isAuthenticated } = useAuth();
  const [permission, setPermission] = useState<PushPermissionState>('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [soundHint, setSoundHint] = useState<string | null>(null);
  const autoResubscribeAttempted = useRef(false);

  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  useEffect(() => {
    if (!supported) {
      setPermission('unsupported');
      return;
    }
    setPermission(Notification.permission as PushPermissionState);
    registerServiceWorker();
  }, [supported]);

  const refreshLocalState = useCallback(async () => {
    if (!supported) return;
    setPermission(Notification.permission as PushPermissionState);
    const hasLocal = await syncLocalSubscription();
    setSubscribed(hasLocal);
  }, [supported]);

  useEffect(() => {
    refreshLocalState();
  }, [refreshLocalState, isAuthenticated]);

  const subscribe = useCallback(
    async (options?: { showTest?: boolean }) => {
      if (!supported) return false;
      setLoading(true);
      setError(null);
      setSoundHint(null);
      try {
        primeNotificationAudio();
        const registration = await getServiceWorkerRegistration();
        const vapidKey = await resolveVapidPublicKey();

        let currentPermission = Notification.permission;
        if (currentPermission === 'default') {
          const result = await Notification.requestPermission();
          currentPermission = result;
          setPermission(result as PushPermissionState);
        } else {
          setPermission(currentPermission as PushPermissionState);
        }

        if (currentPermission !== 'granted') {
          throw new Error(
            currentPermission === 'denied'
              ? 'Notifications are blocked in your browser settings'
              : 'Notification permission was not granted'
          );
        }

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
          });
        }

        const json = subscription.toJSON();
        if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
          throw new Error('Invalid push subscription from browser');
        }

        await pushApi.subscribe({
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        });

        setSubscribed(true);
        setPermission('granted');

        const showTest = options?.showTest !== false;
        if (showTest) {
          const testOptions: NotificationOptions & { renotify?: boolean; vibrate?: number[] } = {
            body: 'You will receive alerts for friend requests and messages.',
            icon: '/vite.svg',
            tag: `moviemind-enabled-${Date.now()}`,
            renotify: true,
            silent: false,
            vibrate: [200, 100, 200],
          };
          await registration.showNotification(
            'Notifications enabled!',
            testOptions as NotificationOptions
          );
          // Also play in-app chime so user hears something even if OS mutes push
          playNotificationChime();
          setSoundHint(
            "If you didn't hear a sound, check your OS notification settings for your browser (sounds must be enabled for site notifications)."
          );
        }

        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to enable notifications');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [supported]
  );

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    setError(null);
    try {
      const registration = await navigator.serviceWorker.getRegistration('/');
      const subscription = await registration?.pushManager.getSubscription();
      const endpoint = subscription?.endpoint;
      await pushApi.unsubscribe(endpoint);
      await subscription?.unsubscribe();
      setSubscribed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable notifications');
    } finally {
      setLoading(false);
    }
  }, [supported]);

  // Re-sync with backend when permission already granted (e.g. after login or expired sub)
  useEffect(() => {
    if (!isAuthenticated || !supported || permission !== 'granted') {
      autoResubscribeAttempted.current = false;
      return;
    }
    if (autoResubscribeAttempted.current) return;
    autoResubscribeAttempted.current = true;

    (async () => {
      const hasLocal = await syncLocalSubscription();
      if (!hasLocal) {
        await subscribe({ showTest: false });
      } else {
        const registration = await navigator.serviceWorker.getRegistration('/');
        const subscription = await registration?.pushManager.getSubscription();
        const json = subscription?.toJSON();
        if (json?.endpoint && json.keys?.p256dh && json.keys?.auth) {
          try {
            await pushApi.subscribe({
              endpoint: json.endpoint,
              keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
            });
            setSubscribed(true);
          } catch {
            // best-effort sync
          }
        }
      }
    })();
  }, [isAuthenticated, supported, permission, subscribe]);

  return {
    supported,
    permission,
    subscribed,
    loading,
    error,
    soundHint,
    subscribe,
    unsubscribe,
  };
}
